/**
 * ErrorBoundary — Sprint BQ.
 *
 * Wraps each top-level page so a render-time exception in one
 * route doesn't take down the whole shell. Without it, a stray
 * `Cannot read properties of undefined` in any page collapses
 * the admin UI to a blank screen and operators reach for the
 * page refresh — which often won't help because the bug is
 * deterministic given the data they have.
 *
 * What the boundary does:
 *   - Catches every render / lifecycle / effect-throw inside
 *     its children.
 *   - Renders a fallback with the error message + a "Reload
 *     page" button (forces a fresh router state by reloading
 *     the SPA) + a "Reset" button (clears the boundary's own
 *     state so the user can try interacting with the page
 *     again without losing their session).
 *   - Logs the error to the console via console.error so
 *     operators screenshotting devtools get something useful.
 *
 * No Sentry / network reporter here on purpose — the admin UI
 * doesn't currently have a target. Plumbing one in is a
 * follow-up: add an `onError` prop and have App pass a
 * dispatch callback.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertOctagon, RefreshCw, RotateCcw } from "lucide-react";
import { Button } from "@vseh/ui";

import { useT } from "../i18n";


/**
 * Fallback content extracted to a functional component so it can
 * call useT. The class boundary can't use hooks directly — this
 * sub-component is the bridge.
 */
function ErrorFallback(props: {
  error:       Error;
  // `string | undefined` (not `scopeLabel?: string`) so a caller
  // can pass `scopeLabel={parent.scopeLabel}` when the parent's
  // optional prop is `undefined` and `exactOptionalPropertyTypes`
  // is on — the prop's type carries the same shape as the source.
  scopeLabel:  string | undefined;
  onReset:     () => void;
  onReload:    () => void;
}): JSX.Element {
  const t = useT();
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-3 rounded-lg border border-danger-200 bg-danger-50 p-5"
    >
      <div className="flex items-start gap-3">
        <AlertOctagon
          aria-hidden="true"
          className="mt-0.5 size-6 shrink-0 text-danger-700"
        />
        <div className="min-w-0">
          <div className="font-display text-base font-semibold text-danger-900">
            {t("errboundary.title")}
            {props.scopeLabel
              ? t("errboundary.scope", { scope: props.scopeLabel })
              : ""}
          </div>
          <div className="mt-1 break-words text-sm text-danger-800">
            {props.error.message || t("errboundary.unknown")}
          </div>
          <div className="mt-1 text-2xs text-danger-700">
            {t("errboundary.devnote")}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="primary" size="sm"
          leftIcon={<RotateCcw className="size-3.5" />}
          onClick={props.onReset}
        >
          {t("errboundary.retry")}
        </Button>
        <Button
          variant="ghost" size="sm"
          leftIcon={<RefreshCw className="size-3.5" />}
          onClick={props.onReload}
        >
          {t("errboundary.reload")}
        </Button>
      </div>
    </div>
  );
}


interface Props {
  children:  ReactNode;
  /** Optional identifier so the fallback can name the page that
   *  blew up. Useful when multiple boundaries nest. */
  scopeLabel?: string;
}


interface State {
  error: Error | null;
}


export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The structured log is what shows up in production browser
    // consoles — operators copy-paste this into incident reports.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", this.props.scopeLabel ?? "(root)", {
      message:        error.message,
      stack:          error.stack,
      componentStack: info.componentStack,
    });
  }

  private reset = (): void => this.setState({ error: null });

  private reload = (): void => {
    // Hard reload — discards the broken page's local state cleanly,
    // forces a fresh JWT check, re-issues the data fetches.
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children;
    return (
      <ErrorFallback
        error={this.state.error}
        scopeLabel={this.props.scopeLabel}
        onReset={this.reset}
        onReload={this.reload}
      />
    );
  }
}
